/**
 * YDMPageHero — the title band every page opens on.
 *
 * It used to be a solid yellow poster: primary-500 fill, a 2px black outline, a
 * hard offset shadow, two blurred colour blobs and a 3xl italic headline. One of
 * those on a landing page is a statement; one on top of fifteen consecutive
 * screens is wallpaper, and it was the loudest element on every page while
 * carrying the least information on it.
 *
 * Toning it down was only half the fix, and the first attempt got the other half
 * wrong: a white card with a yellow edge is made of exactly the same material as
 * every content card below it — white, rounded, bordered, shadowed — so it
 * stopped shouting and started reading as the first card on the page rather than
 * as the page's title.
 *
 * SO THIS IS NOT A CARD AT ALL. The page ground is grey and every block of
 * content is a white card; a title that sits directly on the grey therefore
 * cannot be mistaken for content, and the distinction is structural rather than
 * decorative — nothing to learn, nothing to out-colour. With no box competing
 * for attention the headline can also be larger than it was inside one, which
 * is where the prominence comes from now.
 *
 * The brand is carried by the wordmark and the sidebar, which are always on
 * screen, so this band needs no yellow of its own. (A short yellow rule above
 * the eyebrow is a one-line addition if it is ever wanted.)
 *
 * The props are unchanged, so all fifteen pages inherit this without edits.
 *
 * @module components/brand/YDMPageHero
 */

import React from 'react';
import { clsx } from 'clsx';

export interface YDMPageHeroProps {
  /** Small eyebrow above the headline, e.g. page name. */
  eyebrow?: string;
  /** Main headline. Defaults to the brand slogan. */
  headline?: string;
  /** Sub-line under the headline. */
  subtitle?: string;
  /** Optional right-side slot for buttons / filters / status pills. */
  actions?: React.ReactNode;
  className?: string;
}

export const YDMPageHero: React.FC<YDMPageHeroProps> = ({
  eyebrow,
  headline = 'MAKE IT UNSTOPPABLE',
  subtitle,
  actions,
  className,
}) => {
  return (
    <section
      data-page-hero
      className={clsx(
        // No background, no border, no shadow, no radius — every one of those
        // is what makes the blocks below it read as content.
        'px-1 pt-1 mb-6',
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            // Was uppercase italic at 0.3em tracking — a second headline
            // competing with the first. Now a label.
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-700">
              {eyebrow}
            </p>
          )}
          {/* Bigger than it was in the box: with nothing around it competing,
              size is what marks this as the title. */}
          <h1 className="mt-1 font-display italic font-bold text-2xl sm:text-[27px] text-ink-900 leading-[1.15]">
            {headline}
          </h1>
          {subtitle && (
            <p className="mt-1.5 text-sm text-neutral-700 max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>

        {actions && (
          // flex-wrap, and NOT shrink-0. With shrink-0 the three controls a
          // page typically puts here — a freshness pill, the period selector
          // and one button — refused to shrink below 425px, which is wider
          // than a phone: the whole page scrolled sideways on every screen
          // that uses this band.
          <div className="flex flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </section>
  );
};

export default YDMPageHero;
